import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { setActiveRole, type UserRole } from "@/lib/auth";
import { useAuthUser } from "@/lib/use-auth-user";

import { InlineAlert } from "@/components/ui/States";

const ROLE_LABEL: Record<UserRole, string> = {
  manager: "Organizer",
  recruiter: "Recruiter",
  candidate: "Candidate",
  admin: "Admin",
  participant: "Participant",
} as const;

const SWITCHABLE: ReadonlySet<UserRole> = new Set([
  "manager",
  "recruiter",
  "candidate",
  "participant",
]);

const LANDING_FOR: Record<UserRole, string> = {
  manager: "/events",
  recruiter: "/events",
  candidate: "/events",
  participant: "/me/meetups",
  admin: "/events",
};

export function RoleSwitcher() {
  const { user } = useAuthUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<UserRole | null>(null);

  if (!user) return null;

  const switchableRoles = user.roles.filter((r) => SWITCHABLE.has(r));
  if (switchableRoles.length < 2) return null;

  const onChange = async (next: UserRole) => {
    if (next === user.activeRole) return;
    setError(null);
    setPending(next);
    try {
      await setActiveRole(next);
      // V1 of this used invalidateQueries(), which marks the cache
      // stale and kicks off a refetch but does NOT await its
      // completion. navigate() then fires while the cached
      // activeRole still reads as the previous value, and the next
      // page renders for the wrong role for a frame. Use
      // refetchQueries() so we wait for the refresh to land before
      // routing.
      await queryClient.refetchQueries({
        queryKey: ["auth-user-profile", user.id],
        exact: true,
      });
      navigate(LANDING_FOR[next], { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not switch role.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="rounded-[12px] border border-[#e2e8f0] bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
        Active role
      </p>
      <p className="mt-1 text-[13px] text-[#475569]">
        Switch the context you want to use.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {switchableRoles.map((role) => {
          const isActive = user.activeRole === role;
          const isPending = pending === role;
          return (
            <button
              key={role}
              type="button"
              disabled={isPending || isActive}
              onClick={() => onChange(role)}
              className={`rounded-[8px] border px-3 py-2 text-[13px] font-semibold transition ${
                isActive
                  ? "border-[#2563eb] bg-[#2563eb] text-white"
                  : "border-[#e2e8f0] bg-white text-[#475569] hover:border-[#2563eb] hover:text-[#2563eb]"
              } ${isPending ? "opacity-50" : ""}`}
            >
              {ROLE_LABEL[role]}
              {isActive ? " · current" : ""}
            </button>
          );
        })}
      </div>

      {error ? (
        <InlineAlert tone="danger" className="mt-3 text-[12px]">
          {error}
        </InlineAlert>
      ) : null}
    </div>
  );
}
